import { Core } from "@faire/design-tokens";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getSettingFaireDirectCreditExpiresInDays } from "@faire/retailer-visitor-shared/settings/getSettingFaireDirectCreditExpiresInDays";
import { useSignUpQueryParams } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { Button } from "@faire/web/slate/Button";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { trackFaireDirectSignUpFlowLinkToSignInClick } from "@faire/web-api--source/events/faireDirect/click/signUpFlowLinkToSignIn";
import brandPage from "@faire/web-api--source/routes/www/brand/brandToken";
import privacyPath from "@faire/web-api--source/routes/www/privacy";
import homePage from "@faire/web-api--source/routes/www/root";
import termsOfService from "@faire/web-api--source/routes/www/tos";
import React, { useCallback, type JSX } from "react";

export const FaireDirectDisclaimer: React.FC = () => {
  const {
    formattedMaxPercentageDiscountAmount,
    isFaireDirectCreditPercentageBased,
    formattedFixedBasedAmount,
    brandName,
  } = useFaireDirectFirstOrderIncentive();

  const promoMaxDiscountMoney = isFaireDirectCreditPercentageBased
    ? formattedMaxPercentageDiscountAmount
    : formattedFixedBasedAmount;

  const daysNumber = getSettingFaireDirectCreditExpiresInDays();

  return (
    <Typography variant="labelSansRegular" color={Core.text.subdued}>
      <StrictLocalMsg
        defaultMessage={
          "*Available for retailers who are new to Faire. Max discount of {promoMaxDiscountMoney} & 1 year of free shipping with {brandCompanyName} only. Expires {daysNumber} days after signup. By proceeding, you're agreeing to our <TosLinkFormatting>Terms</TosLinkFormatting> and <PrivacyLinkFormatting>Privacy Policy</PrivacyLinkFormatting>."
        }
        description={{
          text: "Disclaimer for the Faire Direct signup credit",
          img: "https://cdn.faire.com/fastly/b417df230ff4fb17678bef1014a89125468353f658be8fd0d71abdefab9b7959.png",
        }}
        values={{
          promoMaxDiscountMoney,
          brandCompanyName: brandName ?? "",
          daysNumber,
          TosLinkFormatting,
          PrivacyLinkFormatting,
        }}
      />
    </Typography>
  );
};

export const FaireDirectLoginDisclaimer: React.FC = () => {
  const { brandToken } = useFaireDirectFirstOrderIncentive();
  const { queryParams } = useSignUpQueryParams();

  const LinkFormatting = useCallback(
    (chunks: JSX.Element) => (
      <Button
        tertiaryColor="inherit"
        variant="tertiary"
        style={{ display: "inline" }}
        onClick={() =>
          trackFaireDirectSignUpFlowLinkToSignInClick(queryParams.signUp ?? "")
        }
        linkTo={homePage({
          signIn: "1",
          redirectTo: brandPage(brandToken ?? ""),
        })}
      >
        {chunks}
      </Button>
    ),
    [brandToken]
  );
  return (
    <Typography>
      <StrictLocalMsg
        defaultMessage="Already have an account? <LinkFormatting>Sign In</LinkFormatting>"
        values={{ LinkFormatting }}
      />
    </Typography>
  );
};

const TosLinkFormatting = (chunk: JSX.Element) => (
  <Button
    tertiaryColor="inherit"
    variant="tertiary"
    style={{ display: "inline", fontSize: "inherit" }}
    linkTo={termsOfService()}
  >
    {chunk}
  </Button>
);

const PrivacyLinkFormatting = (chunk: JSX.Element) => (
  <Button
    tertiaryColor="inherit"
    variant="tertiary"
    style={{ display: "inline", fontSize: "inherit" }}
    linkTo={privacyPath()}
  >
    {chunk}
  </Button>
);
